import { Moon, Sun, SunMoon } from 'lucide-react';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  /** What the person chose with the toggle; null until they pick something. */
  preference: ThemePreference | null;
  setPreference: (preference: ThemePreference) => void;
}

/** Client-local UI preference; persisted per browser. Not server state. */
export const useThemeStore = create<ThemeState>()(
  persist((set) => ({ preference: null, setPreference: (preference) => set({ preference }) }), {
    name: 'nucleus.theme',
  }),
);

const DefaultThemeContext = createContext<ThemePreference>('system');

function systemTheme(): ResolvedTheme {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

function applyTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** What the app looks like before the person touches the toggle. */
  defaultPreference?: ThemePreference;
}

/**
 * Applies the `.dark` class to <html> and follows the OS when the effective preference is
 * "system". Each app picks its own default; an explicit toggle always wins over it.
 */
export function ThemeProvider({ children, defaultPreference = 'system' }: ThemeProviderProps) {
  const chosen = useThemeStore((s) => s.preference);
  const preference = chosen ?? defaultPreference;

  useEffect(() => {
    applyTheme(resolveTheme(preference));
    if (preference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => applyTheme(systemTheme());
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [preference]);

  return (
    <DefaultThemeContext.Provider value={defaultPreference}>
      {children}
    </DefaultThemeContext.Provider>
  );
}

export function useTheme() {
  const defaultPreference = useContext(DefaultThemeContext);
  const chosen = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);
  const preference = chosen ?? defaultPreference;
  return { preference, resolved: resolveTheme(preference), setPreference };
}

const OPTIONS: Array<{ value: ThemePreference; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: SunMoon },
];

/** Header button cycling light / dark / system. Labels are intentionally untranslated icons + titles. */
export function ThemeToggle({ label = 'Toggle theme' }: { label?: string }) {
  const { preference, resolved, setPreference } = useTheme();
  const Icon = resolved === 'dark' ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={label}>
          <Icon aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => setPreference(option.value)}
            data-active={preference === option.value}
            className="data-[active=true]:bg-accent"
          >
            <option.icon aria-hidden />
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
